import { useMemo } from 'react';
import { matchPath } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Represents a tab with routing information.
 */
interface RoutedTab {
  /** Unique identifier for the tab */
  id: string;
  /** Route path for the tab */
  route: string;
  /** Any additional properties */
  [key: string]: any;
}

/**
 * Custom hook for managing routed tabs.
 *
 * @param tabs - An array of RoutedTab objects defining the tabs and their routes
 * @returns An object containing the current tab and a function to handle tab changes
 *
 * @example
 * const tabs = [
 *   { id: 'home', route: '/home' },
 *   { id: 'profile', route: '/profile' },
 * ];
 * const { currentTab, handleChange } = useRoutedTabs(tabs);
 */
export function useRoutedTabs(tabs: RoutedTab[]) {
  const history = useHistory();
  const location = useLocation();

  const currentTab = useMemo(() => {
    // Find all matching tabs
    const matchingTabs = tabs.filter((tab) =>
      matchPath(location.pathname, { path: tab.route, exact: false }),
    );

    // If there are matches, sort them by specificity (route length) and return the most specific
    if (matchingTabs.length > 0) {
      return matchingTabs.sort((a, b) => b.route.length - a.route.length)[0];
    }

    // If no match, return the first tab as a fallback
    return tabs[0];
  }, [tabs, location.pathname]);

  const handleChange = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      const projectId = new URLSearchParams(location.search).get('projectId');
      const newPath = `${tab.route}?projectId=${projectId}`;
      history.push(newPath);
    }
  };

  return {
    currentTab,
    handleChange,
  };
}
